/**
 * The prototype's render engine, ported — `m04-engine.js`.
 *
 * One layout solver feeds both renderers upstream; the demo only needs the SVG side, so
 * `planToCanvas` is not ported. Everything the SVG path touches is: the placement solver, the
 * shrink-to-fit text measurement, the six fail-closed plan checks, and the exact string the SVG
 * is assembled from.
 *
 * The output is compared byte for byte against the reference's, so every `toFixed`, every raw
 * number interpolation and every attribute that is emitted only under a condition is reproduced
 * as written rather than tidied. Where upstream lets `undefined` reach a template literal, so
 * does this — adding a fallback would change the bytes.
 */
import { trLower, trUpper } from '../reference';
import { type AssetRef, type RenderAsset, resolveAssetRef, resolveFontRef } from './library';
import { qrMatrix } from './qrcode';

// --------------------------------------------------------------------------- the layout's shape

export interface Canvas {
  width?: number;
  height?: number;
  w?: number;
  h?: number;
  orientation?: string;
}

export interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Focal {
  x: number;
  y: number;
}

export interface Layer {
  id: string;
  kind?: string;
  hidden?: boolean;
  zIndex?: number;
  x: number;
  y: number;
  width?: number | null;
  height?: number | null;
  fieldKey?: string;
  text?: string;
  font?: string;
  weight?: number;
  style?: string;
  color?: string;
  align?: string;
  verticalAlign?: string;
  fontSizeRatio?: number;
  minFontSizeRatio?: number;
  letterSpacing?: number;
  lineHeight?: number;
  maxLines?: number;
  fitMode?: string;
  textTransform?: string;
  /** The fold's language, pinned into the layer when its release was published; absent ⇒ Turkish. */
  textTransformLocale?: string;
  opacity?: number;
  rotation?: number;
  assetRef?: AssetRef | null;
  imageFit?: string;
  crop?: CropRect | null;
}

export interface FieldDefinition {
  key: string;
  labels?: { tr?: string; en?: string };
  required?: boolean;
  defaultValue?: string | number | null;
  sample?: string | null;
  /**
   * The field's own data type, as the contract names it — part of the layout the editor writes.
   *
   * The DRAWING does not branch on it. It used to: a `date` field's value was written in the language
   * being drawn, which turned out to be wider than the server's own rule — the server localizes the
   * two auto keys `issue_date` and `expiry_date` and hands every other field its claim verbatim, so a
   * template's own date field localized here promised something the sealed document does not say.
   * Which fields are drawn as a localized date is decided by KEY, in one place
   * (`isLocalizedDateField`, `../reference`), against a contract both packages read.
   */
  dataType?: string;
}

export interface Background {
  assetRef?: AssetRef | null;
  fitMode?: string;
  x?: number;
  y?: number;
  scale?: number;
  crop?: CropRect | null;
  focal?: Focal | null;
  rotation?: number;
  opacity?: number;
}

export interface FontFace {
  fontAssetId?: string;
  sha256?: string;
  weight?: number;
  style?: string;
}

export interface FontReference {
  family: string;
  faces?: FontFace[];
}

export interface Layout {
  canvas: Canvas;
  background?: Background | null;
  layers?: Layer[];
  fieldDefinitions?: FieldDefinition[];
  fontReferences?: FontReference[];
  holderField?: string;
  localizedMetadata?: Record<string, { title?: string }>;
  subjectType?: string;
}

export type RenderData = Record<string, string | number | null | undefined>;

export interface OutputSpec {
  width?: number;
  height?: number;
  format?: string;
  scale?: number;
  colorProfile?: string;
}

// ------------------------------------------------------------------------------ small utilities

/** `m01-utils.js` — the five XML entities, in that order. */
export const esc = (s: unknown): string =>
  String(s ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string,
  );

export const clamp = (v: number, a: number, b: number): number => Math.min(b, Math.max(a, v));

/**
 * `m01-utils.js` — case folding by the text's own language; `reference.ts` has the Turkish halves.
 *
 * The fold was Turkish for every layer, so an English field set to uppercase drew `CERTİFİED`. The
 * Product Owner approved folding by the text's language for NEW drafts and NEW outputs, and
 * expressly not for what is already published, so the language is PINNED into the layer's own
 * immutable style when its release is published — never read from a setting at draw time.
 *
 * Absent means Turkish, which is what every layer drawn before this change carries: the preview of
 * an older release is byte-for-byte the drawing it always was. The rule is the server's
 * (`backend/src/domain/render/text-transform.ts`) and the two are held to one shared fixture.
 */
export const applyTransform = (s: unknown, mode: string | undefined, locale?: string): string => {
  const english = locale === 'en';
  if (mode === 'uppercase') return english ? String(s ?? '').toUpperCase() : trUpper(String(s ?? ''));
  if (mode === 'lowercase') return english ? String(s ?? '').toLowerCase() : trLower(String(s ?? ''));
  return String(s ?? '');
};

export const canvasW = (c: Canvas | undefined): number => (c && (c.width ?? c.w)) || 1;
export const canvasH = (c: Canvas | undefined): number => (c && (c.height ?? c.h)) || 1;

export const fieldLabel = (f: FieldDefinition, lang: 'tr' | 'en'): string =>
  (f.labels ? (lang === 'en' ? f.labels.en : f.labels.tr) : null) || f.key;

/** `m02-mockdb.js` — the families a layout draws text in, in first-seen order. */
export const layoutFonts = (d: Layout): (string | undefined)[] => [
  ...new Set((d.layers || []).filter((l) => l.kind === 'text' || l.kind === 'static').map((l) => l.font)),
];

/** `m01-utils.js` — sorted keys, dropped `undefined`, so the same content hashes the same. */
export function canonicalJSON(v: unknown): string {
  const walk = (x: unknown): unknown => {
    if (x === null || typeof x === 'number' || typeof x === 'boolean' || typeof x === 'string') return x;
    if (x === undefined) return undefined;
    if (Array.isArray(x)) return x.map((i) => (i === undefined ? null : walk(i)));
    if (typeof x === 'object') {
      const out: Record<string, unknown> = {};
      for (const k of Object.keys(x).sort()) {
        const w = walk((x as Record<string, unknown>)[k]);
        if (w !== undefined) out[k] = w;
      }
      return out;
    }
    return undefined;
  };
  return JSON.stringify(walk(v));
}

/**
 * `m01-utils.js` — FNV-1a twice, 16 hex characters. Not cryptographic; it exists so a clipPath id
 * is stable across renders of the same plan, which is what keeps two identical renders byte
 * identical. A global counter would not be.
 */
export function syncHashHex(input: unknown): string {
  const str = typeof input === 'string' ? input : canonicalJSON(input);
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 = (h2 ^ (c + i)) >>> 0;
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}

// ------------------------------------------------------------------------------ image placement

export interface Placement {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
  clip: { x: number; y: number; w: number; h: number } | null;
  fitMode: string;
  sourceSize: { width: number; height: number };
  opacity: number;
  rotation: number;
  pivot: { x: number; y: number };
}

interface PlacementOptions {
  x?: number | null | undefined;
  y?: number | null | undefined;
  scale?: number | null | undefined;
  crop?: CropRect | null | undefined;
  focal?: Focal | null | undefined;
  opacity?: number | undefined;
  rotation?: number | undefined;
}

interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** `m04-engine.js` line 9 — the single placement solver both renderers share. */
export function resolveImagePlacement(
  asset: { width: number; height: number },
  frame: Frame,
  fitMode: string | undefined,
  o: PlacementOptions = {},
): Placement {
  const aw = Math.max(1, asset.width);
  const ah = Math.max(1, asset.height);
  const fx = frame.x;
  const fy = frame.y;
  const fw = frame.w;
  const fh = frame.h;
  const mode = fitMode || 'contain';
  let sx = 0;
  let sy = 0;
  let sw = aw;
  let sh = ah;
  let dx: number;
  let dy: number;
  let dw: number;
  let dh: number;
  let clip: Frame | null = { x: fx, y: fy, w: fw, h: fh };
  const cx = fx + (o.x != null ? o.x : 0.5) * fw;
  const cy = fy + (o.y != null ? o.y : 0.5) * fh;
  if (mode === 'stretch') {
    dx = fx;
    dy = fy;
    dw = fw;
    dh = fh;
    clip = null;
  } else if (mode === 'crop') {
    const c = o.crop || { x: 0, y: 0, width: 1, height: 1 };
    sx = clamp(c.x, 0, 1) * aw;
    sy = clamp(c.y, 0, 1) * ah;
    sw = Math.max(1, clamp(c.width, 0.001, 1) * aw);
    sh = Math.max(1, clamp(c.height, 0.001, 1) * ah);
    dx = fx;
    dy = fy;
    dw = fw;
    dh = fh;
  } else if (mode === 'contain') {
    const s = Math.min(fw / aw, fh / ah);
    dw = aw * s;
    dh = ah * s;
    dx = cx - dw / 2;
    dy = cy - dh / 2;
    clip = null;
  } else {
    const s = mode === 'cover' ? Math.max(fw / aw, fh / ah) : mode === 'native' ? 1 : o.scale || 1;
    dw = aw * s;
    dh = ah * s;
    if (mode === 'cover' && o.focal) {
      dx = fx + fw / 2 - dw * clamp(o.focal.x, 0, 1);
      dy = fy + fh / 2 - dh * clamp(o.focal.y, 0, 1);
    } else {
      dx = cx - dw / 2;
      dy = cy - dh / 2;
    }
  }
  return {
    sx: +sx.toFixed(4),
    sy: +sy.toFixed(4),
    sw: +sw.toFixed(4),
    sh: +sh.toFixed(4),
    dx: +dx.toFixed(4),
    dy: +dy.toFixed(4),
    dw: +dw.toFixed(4),
    dh: +dh.toFixed(4),
    clip: clip
      ? { x: +clip.x.toFixed(4), y: +clip.y.toFixed(4), w: +clip.w.toFixed(4), h: +clip.h.toFixed(4) }
      : null,
    fitMode: mode,
    sourceSize: { width: aw, height: ah },
    opacity: o.opacity != null ? o.opacity : 1,
    rotation: o.rotation || 0,
    pivot: { x: +(fx + fw / 2).toFixed(4), y: +(fy + fh / 2).toFixed(4) },
  };
}

// ---------------------------------------------------------------------- text measurement + fit

/**
 * Upstream measures with one detached 2D context created at module load. Here it is created on
 * first use, so importing the engine outside a browser (a unit test, a build step) does not throw
 * at import time. Without a context `measureText` has no answer and every line reports width 0,
 * which means shrink-to-fit never shrinks — the engine is a browser renderer and the demo only
 * ever calls it from one.
 */
let measureCtx: CanvasRenderingContext2D | null | undefined;
function context2d(): CanvasRenderingContext2D | null {
  if (measureCtx === undefined) {
    measureCtx = typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  }
  return measureCtx;
}

export const fontStr = (l: Layer, size: number): string =>
  `${l.style === 'italic' ? 'italic ' : ''}${l.weight || 400} ${size}px "${l.font || 'Poppins'}", sans-serif`;

const spacingPx = (l: Layer, size: number): number => ((l.letterSpacing || 0) * size) / 100;

export const sizePx = (l: Layer, canvas: Canvas): number =>
  Math.max(4, Math.round((l.fontSizeRatio ?? 0.04) * canvasW(canvas)));

export const minPx = (l: Layer, canvas: Canvas): number =>
  Math.max(4, Math.round((l.minFontSizeRatio ?? (l.fontSizeRatio ?? 0.04) * 0.6) * canvasW(canvas)));

/** Letter spacing is added between glyphs, so n−1 gaps, never after the last one. */
export function measure(text: string, l: Layer, size: number): number {
  const ctx = context2d();
  if (!ctx) return 0;
  ctx.font = fontStr(l, size);
  return ctx.measureText(text).width + spacingPx(l, size) * Math.max(0, text.length - 1);
}

export function wrap(text: string, l: Layer, size: number, maxW: number): string[] {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (!cur || measure(t, l, size) <= maxW) cur = t;
    else {
      lines.push(cur);
      cur = w;
    }
  }
  if (cur) lines.push(cur);
  return lines.length ? lines : [''];
}

export interface Fit {
  size: number;
  lines: string[];
  overflow: boolean;
  width: number;
  maxW: number;
  text: string;
  base: number;
  floor: number;
}

/**
 * Shrink-to-fit: one whole pixel at a time from the layer's size down to its minimum.
 *
 * `draftFoldLocale` is the DRAFT's declared language, and only a caller drawing a draft passes it —
 * the editor's own stage and the publish gate's fit probe. It is deliberately a parameter rather
 * than something read off the layout, because a layout is also a published release here, and a
 * release's fold is whatever was pinned into its layers when it was published. A caller that passes
 * nothing gets exactly the fold this function has always applied.
 */
export function fitText(l: Layer, rawText: string, canvas: Canvas, draftFoldLocale?: string): Fit {
  const text = applyTransform(
    rawText,
    l.textTransform,
    /* Pinned at publish. A draft has no pin yet, so it folds by the language it declares; a release
       published before this change has none either, and folds exactly as it always did. */
    l.textTransformLocale ?? draftFoldLocale,
  );
  const maxW = (l.width ?? 0.6) * canvasW(canvas);
  const maxLines = Math.max(1, l.maxLines || 1);
  const mode = l.fitMode || 'shrink';
  const base = sizePx(l, canvas);
  const floor = Math.min(base, minPx(l, canvas));
  const tryAt = (s: number, forceWrap: boolean) => {
    const lines = forceWrap || maxLines > 1 ? wrap(text, l, s, maxW) : [text];
    const widths = lines.map((x) => measure(x, l, s));
    const ok = lines.length <= maxLines && widths.every((wd) => wd <= maxW);
    return { size: s, lines: lines.slice(0, maxLines), ok, width: Math.max(...widths) };
  };
  if (mode === 'fixed') {
    const r = tryAt(base, false);
    return { size: r.size, lines: r.lines, overflow: !r.ok, width: r.width, maxW, text, base, floor };
  }
  if (mode === 'wrap') {
    const r = tryAt(base, true);
    return { size: r.size, lines: r.lines, overflow: !r.ok, width: r.width, maxW, text, base, floor };
  }
  for (let s = base; s >= floor; s--) {
    const r = tryAt(s, false);
    if (r.ok) return { size: s, lines: r.lines, overflow: false, width: r.width, maxW, text, base, floor };
  }
  const r = tryAt(floor, false);
  return { size: floor, lines: r.lines, overflow: true, width: r.width, maxW, text, base, floor };
}

// ------------------------------------------------------------------------------ value resolution

const fieldDefOf = (layout: Layout, key: string | undefined): FieldDefinition | null =>
  (layout.fieldDefinitions || []).find((f) => f.key === key) || null;

export type ValueSource = 'static' | 'data' | 'unknown_field' | 'default' | 'sample' | 'label';

export interface ResolvedValue {
  text: string;
  source: ValueSource;
  missing: boolean;
  def: FieldDefinition | null;
}

/**
 * §5 — where a value came from is on the record. Filling a missing issuance value from the
 * field's sample or its label is allowed only as a design preview; the plan reports it as
 * `missing_required_value` so a real issuance cannot publish a certificate with a placeholder on
 * it.
 */
export function resolveValue(layout: Layout, l: Layer, data: RenderData | null | undefined): ResolvedValue {
  if (l.kind === 'static') return { text: l.text || '', source: 'static', missing: false, def: null };
  const d = fieldDefOf(layout, l.fieldKey);
  const v = data && l.fieldKey !== undefined ? data[l.fieldKey] : undefined;
  if (v != null && String(v).trim() !== '')
    return { text: String(v), source: 'data', missing: false, def: d };
  if (!d) return { text: String(l.fieldKey || ''), source: 'unknown_field', missing: true, def: null };
  if (d.defaultValue != null && String(d.defaultValue).trim() !== '')
    return { text: String(d.defaultValue), source: 'default', missing: false, def: d };
  if (d.sample != null && String(d.sample).trim() !== '')
    return { text: String(d.sample), source: 'sample', missing: true, def: d };
  return { text: fieldLabel(d, 'tr'), source: 'label', missing: true, def: d };
}

export const ordered = (layout: Layout): Layer[] =>
  (layout.layers || [])
    .filter((l) => !l.hidden)
    .slice()
    .sort((a, b) => (a.zIndex ?? 10) - (b.zIndex ?? 10));

interface ResolvedQr {
  layer: Layer;
  kind: 'qr';
  text: string;
  matrix: boolean[][] | null;
}

interface ResolvedImage {
  layer: Layer;
  kind: 'image';
  asset: RenderAsset | null;
  refError: string | null;
}

interface ResolvedText extends Fit {
  layer: Layer;
  kind: 'text';
  valueSource: ValueSource;
  valueMissing: boolean;
  fieldDef: FieldDefinition | null;
}

type Resolved = ResolvedQr | ResolvedImage | ResolvedText;

function resolve(layout: Layout, data: RenderData | null | undefined, draftFoldLocale?: string): Resolved[] {
  const canvas = layout.canvas;
  return ordered(layout).map((l): Resolved => {
    if (l.kind === 'qr') {
      const fromData =
        data && (data.verification_url || (l.fieldKey !== undefined ? data[l.fieldKey] : undefined));
      const url = String(fromData || fieldDefOf(layout, l.fieldKey)?.sample || '');
      return { layer: l, kind: 'qr', text: url, matrix: qrMatrix(url) };
    }
    if (l.kind === 'image') {
      const r = l.assetRef ? resolveAssetRef(l.assetRef) : ({ ok: false, reason: 'missing_ref' } as const);
      return { layer: l, kind: 'image', asset: r.ok ? r.asset : null, refError: r.ok ? null : r.reason };
    }
    const rv = resolveValue(layout, l, data);
    return {
      layer: l,
      kind: 'text',
      valueSource: rv.source,
      valueMissing: rv.missing,
      fieldDef: rv.def,
      ...fitText(l, rv.text, canvas, draftFoldLocale),
    };
  });
}

// ------------------------------------------------------------------------------------- geometry

export interface BoxGeo {
  x: number;
  y: number;
  size: number;
  w: number;
  h: number;
}

export interface TextGeo {
  x: number;
  y: number;
  lh: number;
  top: number;
  blockH: number;
  baselines: number[];
  anchor: string;
}

function boxGeometry(canvas: Canvas, l: Layer): BoxGeo {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const x = l.x * W;
  const y = l.y * H;
  const w = (l.width ?? 0.1) * W;
  const h = l.height ? l.height * H : w;
  return { x: x - w / 2, y: y - h / 2, size: w, w, h };
}

/** `.35 × size` is the reference's stand-in for the baseline offset inside the line box. */
function textGeometry(canvas: Canvas, l: Layer, r: Fit): TextGeo {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const x = l.x * W;
  const y = l.y * H;
  const lh = r.size * (l.lineHeight || 1.15);
  const n = r.lines.length;
  const blockH = n * lh;
  const top = l.verticalAlign === 'top' ? y : l.verticalAlign === 'bottom' ? y - blockH : y - blockH / 2;
  const baselines = r.lines.map((_, i) => top + lh * (i + 0.5) + r.size * 0.35);
  return {
    x,
    y,
    lh,
    top,
    blockH,
    baselines,
    anchor: l.align === 'left' ? 'start' : l.align === 'right' ? 'end' : 'middle',
  };
}

// --------------------------------------------------------------- the shared, fail-closed plan

export const PLAN_FAILURE_CODES = Object.freeze([
  'layout_overflow',
  'outside_canvas',
  'missing_required_value',
  'asset_unresolved',
  'font_unverified',
  'invalid_layer_geometry',
] as const);

export type PlanFailureCode = (typeof PLAN_FAILURE_CODES)[number];

export const PLAN_FAILURE_TR: Record<PlanFailureCode, string> = Object.freeze({
  layout_overflow: 'Metin ayrılan alana sığmıyor.',
  outside_canvas: 'Katman tuvalin dışına taşıyor.',
  missing_required_value: 'Zorunlu alan için gerçek değer yok; örnek/etiket ile doldurulamaz.',
  asset_unresolved: 'Görsel çözülemedi (bulunamadı veya digest uyuşmuyor).',
  font_unverified: 'Katman fontu doğrulanmış bir font referansına bağlı değil.',
  invalid_layer_geometry: 'Katman geometrisi geçersiz.',
});

export interface PlanFailure extends Record<string, unknown> {
  code: PlanFailureCode;
  where: string;
  reason: PlanFailureCode;
  message: string;
}

export interface PlanItem {
  kind: 'image' | 'qr' | 'text';
  layer: Layer;
  index: number;
  missing?: boolean;
  assetId?: string;
  sha256?: string;
  dataUrl?: string;
  placement?: Placement;
  geo?: BoxGeo | TextGeo;
  text?: string;
  matrix?: boolean[][] | null;
  size?: number;
  lines?: string[];
  overflow?: boolean;
  width?: number;
  maxW?: number;
  valueSource?: ValueSource;
  valueMissing?: boolean;
  opacity?: number;
  rotation?: number;
  letterSpacingPx?: number;
}

export interface PlanBackground {
  assetId: string;
  sha256: string;
  dataUrl: string;
  placement: Placement;
}

export interface RenderPlan {
  ok: boolean;
  failures: PlanFailure[];
  failureCodes: PlanFailureCode[];
  strict: boolean;
  canvas: { width: number; height: number };
  output: Required<OutputSpec>;
  background: PlanBackground | null;
  items: PlanItem[];
  assetRefs: { id: string; sha256: string; role: string }[];
  fonts: (string | undefined)[];
  verifiedFontFamilies: string[];
  renderPlanHash: string;
}

const finiteNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

/** A family counts as verified only when every face its release names resolves in the library. */
export function verifiedFontFamilies(layout: Layout): Set<string> {
  const out = new Set<string>();
  for (const fr of layout.fontReferences || []) {
    const faces = fr.faces || [];
    if (!faces.length) continue;
    const allOk = faces.every((f) => resolveFontRef({ fontAssetId: f.fontAssetId, sha256: f.sha256 }).ok);
    if (allOk) out.add(fr.family);
  }
  return out;
}

export function resolveRenderPlan(
  layout: Layout,
  data: RenderData | null | undefined,
  outputSpec: OutputSpec = {},
  /**
   * `foldLocale`: the language a DRAFT declares, passed only by callers drawing a draft. Left out
   * everywhere else — including every path that draws a published release, whose fold was pinned
   * into its own layers at publish and must not move.
   */
  planOpts: { strict?: boolean; foldLocale?: string } = {},
): RenderPlan {
  const canvas = layout.canvas;
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const failures: PlanFailure[] = [];
  const assetRefs: { id: string; sha256: string; role: string }[] = [];
  const fail = (code: PlanFailureCode, where: string, extra?: Record<string, unknown>): void => {
    failures.push({ code, where, reason: code, message: PLAN_FAILURE_TR[code], ...(extra || {}) });
  };
  const strict = planOpts.strict === true;
  const verifiedFams = verifiedFontFamilies(layout);
  const enforceFonts = (layout.fontReferences || []).length > 0;
  const tol = 0.75;
  const outsideCheck = (where: string, x: number, y: number, w: number, h: number): void => {
    if (![x, y, w, h].every(finiteNum)) {
      fail('invalid_layer_geometry', where, { box: { x, y, w, h } });
      return;
    }
    if (w <= 0 || h <= 0) {
      fail('invalid_layer_geometry', where, { box: { x, y, w, h }, detail: 'non_positive_size' });
      return;
    }
    if (x < -tol || y < -tol || x + w > W + tol || y + h > H + tol) {
      fail('outside_canvas', where, {
        box: { x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) },
        canvas: { width: W, height: H },
      });
    }
  };

  let background: PlanBackground | null = null;
  const bg = layout.background;
  if (bg?.assetRef) {
    const res = resolveAssetRef(bg.assetRef);
    if (!res.ok) {
      fail('asset_unresolved', 'background', {
        detail: res.reason,
        assetId: bg.assetRef.assetId,
        expected: res.expected,
        actual: res.actual,
      });
    } else {
      assetRefs.push({ id: res.asset.id, sha256: res.asset.sha256, role: 'background' });
      background = {
        assetId: res.asset.id,
        sha256: res.asset.sha256,
        dataUrl: res.asset.dataUrlForPrototype,
        placement: resolveImagePlacement(res.asset, { x: 0, y: 0, w: W, h: H }, bg.fitMode, {
          x: bg.x,
          y: bg.y,
          scale: bg.scale,
          crop: bg.crop,
          focal: bg.focal,
          opacity: bg.opacity ?? 1,
          rotation: bg.rotation || 0,
        }),
      };
    }
  }

  const items = resolve(layout, data, planOpts.foldLocale).map((r, index): PlanItem => {
    const l = r.layer;
    const where = `layer:${l.id}`;
    if (!finiteNum(l.x) || !finiteNum(l.y))
      fail('invalid_layer_geometry', where, { detail: 'x_y_not_finite', x: l.x, y: l.y });

    if (r.kind === 'image') {
      const g = boxGeometry(canvas, l);
      if (!r.asset) {
        fail('asset_unresolved', where, {
          detail: r.refError || 'missing_asset',
          assetId: l.assetRef?.assetId,
        });
        return { kind: 'image', layer: l, index, missing: true };
      }
      assetRefs.push({ id: r.asset.id, sha256: r.asset.sha256, role: 'layer' });
      const placement = resolveImagePlacement(
        r.asset,
        { x: g.x, y: g.y, w: g.w, h: g.h },
        l.imageFit || 'contain',
        {
          crop: l.crop,
          opacity: l.opacity ?? 1,
          rotation: l.rotation || 0,
        },
      );
      const box = placement.clip || { x: placement.dx, y: placement.dy, w: placement.dw, h: placement.dh };
      outsideCheck(where, box.x, box.y, box.w, box.h);
      return {
        kind: 'image',
        layer: l,
        index,
        assetId: r.asset.id,
        sha256: r.asset.sha256,
        dataUrl: r.asset.dataUrlForPrototype,
        placement,
      };
    }

    if (r.kind === 'qr') {
      const g = boxGeometry(canvas, l);
      outsideCheck(where, g.x, g.y, g.size, g.size);
      if (!r.matrix)
        fail('missing_required_value', where, { detail: 'qr_matrix_unavailable', field: l.fieldKey });
      return {
        kind: 'qr',
        layer: l,
        index,
        geo: g,
        text: r.text,
        matrix: r.matrix,
        opacity: l.opacity ?? 1,
        rotation: l.rotation || 0,
      };
    }

    const g = textGeometry(canvas, l, r);
    if (r.overflow) {
      fail('layout_overflow', where, {
        field: l.fieldKey,
        width: +Number(r.width || 0).toFixed(2),
        maxWidth: +Number(r.maxW || 0).toFixed(2),
        lines: r.lines.length,
        maxLines: Math.max(1, l.maxLines || 1),
        fontSize: r.size,
      });
    }
    const def = r.fieldDef;
    const isRequired = !!def?.required || l.fieldKey === layout.holderField;
    if (r.valueMissing && (strict || isRequired)) {
      fail('missing_required_value', where, {
        field: l.fieldKey,
        valueSource: r.valueSource,
        required: isRequired,
      });
    }
    if (enforceFonts && (l.kind === 'text' || l.kind === 'static') && l.font && !verifiedFams.has(l.font)) {
      fail('font_unverified', where, { family: l.font, verified: Array.from(verifiedFams) });
    }
    const anchorLeft = l.align === 'left' ? g.x : l.align === 'right' ? g.x - r.width : g.x - r.width / 2;
    outsideCheck(where, anchorLeft, g.top, Math.max(1, r.width), Math.max(1, g.blockH));
    return {
      kind: 'text',
      layer: l,
      index,
      geo: g,
      size: r.size,
      lines: r.lines,
      overflow: r.overflow,
      width: r.width,
      maxW: r.maxW,
      valueSource: r.valueSource,
      valueMissing: r.valueMissing,
      opacity: l.opacity ?? 1,
      rotation: l.rotation || 0,
      letterSpacingPx: spacingPx(l, r.size),
    };
  });

  const output: Required<OutputSpec> = {
    width: W,
    height: H,
    format: 'png',
    scale: 1,
    colorProfile: 'sRGB',
    ...outputSpec,
  };
  const plan: RenderPlan = {
    ok: failures.length === 0,
    failures,
    failureCodes: [...new Set(failures.map((f) => f.code))],
    strict,
    canvas: { width: W, height: H },
    output,
    background,
    items,
    assetRefs,
    fonts: layoutFonts(layout),
    verifiedFontFamilies: Array.from(verifiedFams),
    renderPlanHash: '',
  };
  plan.renderPlanHash = syncHashHex(canonicalPlanShape(plan));
  return plan;
}

/**
 * §6 — what the plan hash is taken over: the layout, not the bytes. Colour, font, weight and
 * spacing are in it on purpose; a layout that renders differently is not the same plan even when
 * every coordinate matches.
 */
export function canonicalPlanShape(plan: RenderPlan): unknown {
  return {
    canvas: plan.canvas,
    output: plan.output,
    background: plan.background
      ? {
          assetId: plan.background.assetId,
          sha256: plan.background.sha256,
          placement: plan.background.placement,
        }
      : null,
    items: plan.items.map((i) => {
      const l = i.layer;
      return {
        i: i.index,
        k: i.kind,
        layer: l.id,
        field: l.fieldKey || null,
        placement: i.placement || null,
        geo: i.geo || null,
        size: i.size ?? null,
        lines: i.lines || null,
        text: i.text || null,
        assetId: i.assetId || null,
        sha256: i.sha256 || null,
        opacity: i.opacity ?? null,
        rotation: i.rotation ?? null,
        missing: !!i.missing,
        color: l.color ?? null,
        font: l.font ?? null,
        weight: l.weight ?? null,
        fontStyle: l.style ?? null,
        align: l.align ?? null,
        letterSpacing: i.letterSpacingPx ?? null,
        lineHeight: l.lineHeight ?? null,
        textTransform: l.textTransform ?? null,
        zIndex: l.zIndex ?? null,
      };
    }),
    fonts: plan.fonts,
  };
}

// ------------------------------------------------------------------------------------------ SVG

export const watermarkSize = (canvas: Canvas, text: string): number => {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  return Math.round(Math.min(Math.min(W, H) * 0.085, (W * 1.05) / (String(text).length * 0.66)));
};

function watermarkSVG(canvas: Canvas, text: string): string {
  const W = canvasW(canvas);
  const H = canvasH(canvas);
  const fs = watermarkSize(canvas, text);
  const bandH = fs * 1.9;
  return `<g transform="rotate(-24 ${W / 2} ${H / 2})"><rect x="${-W * 0.3}" y="${H / 2 - bandH / 2}" width="${W * 1.6}" height="${bandH}" fill="#CF051E" fill-opacity=".86"/><text x="${W / 2}" y="${H / 2 + fs * 0.35}" text-anchor="middle" font-family="Poppins, sans-serif" font-weight="700" font-size="${fs}" letter-spacing="${fs * 0.08}" fill="#FFFFFF">${esc(text)}</text></g>`;
}

/**
 * The clipPath id is `hash(layer id + canonical placement + layer index)`, not a counter: two
 * renders of the same plan have to produce the same bytes, and a counter would make the second
 * one differ for no reason.
 */
function imageSVG(p: Placement, href: string, idSeed: string): string {
  const scaleX = p.dw / p.sw;
  const scaleY = p.dh / p.sh;
  const ix = p.dx - p.sx * scaleX;
  const iy = p.dy - p.sy * scaleY;
  const iw = p.sourceSize.width * scaleX;
  const ih = p.sourceSize.height * scaleY;
  let clipAttr = '';
  let defs = '';
  if (p.clip) {
    const id = `c_${syncHashHex(canonicalJSON({ seed: String(idSeed), placement: p }))}`;
    defs = `<clipPath id="${id}"><rect x="${p.clip.x}" y="${p.clip.y}" width="${p.clip.w}" height="${p.clip.h}"/></clipPath>`;
    clipAttr = ` clip-path="url(#${id})"`;
  }
  const img = `<image href="${href}" x="${ix.toFixed(3)}" y="${iy.toFixed(3)}" width="${iw.toFixed(3)}" height="${ih.toFixed(3)}" preserveAspectRatio="none"${p.opacity < 1 ? ` opacity="${p.opacity}"` : ''}${clipAttr}/>`;
  const body = p.rotation ? `<g transform="rotate(${p.rotation} ${p.pivot.x} ${p.pivot.y})">${img}</g>` : img;
  return (defs ? `<defs>${defs}</defs>` : '') + body;
}

/** Two modules of quiet zone on each side, so the code occupies `size × n/(n+4)`. */
function qrSVG(item: PlanItem): string {
  const g = item.geo as BoxGeo;
  const l = item.layer;
  const m = item.matrix;
  if (!m) {
    return `<g opacity="${item.opacity}"><rect x="${g.x}" y="${g.y}" width="${g.size}" height="${g.size}" fill="#FFFFFF" stroke="#8B837C" stroke-dasharray="6 6" stroke-width="2"/></g>`;
  }
  const n = m.length;
  const quiet = 2;
  const cell = g.size / (n + quiet * 2);
  let rects = '';
  for (let rr = 0; rr < n; rr++) {
    for (let cc = 0; cc < n; cc++) {
      if ((m[rr] as boolean[])[cc]) {
        rects += `<rect x="${(quiet + cc) * cell}" y="${(quiet + rr) * cell}" width="${cell}" height="${cell}"/>`;
      }
    }
  }
  return `<g transform="translate(${g.x.toFixed(2)} ${g.y.toFixed(2)})" opacity="${item.opacity}"><rect width="${g.size}" height="${g.size}" fill="#FFFFFF"/><g fill="${l.color || '#000'}" shape-rendering="crispEdges">${rects}</g></g>`;
}

const rot = (deg: number, cx: number, cy: number, inner: string): string =>
  deg ? `<g transform="rotate(${deg} ${cx.toFixed(1)} ${cy.toFixed(1)})">${inner}</g>` : inner;

export interface SVGOptions {
  responsive?: boolean;
  attrs?: string;
  white?: boolean;
  watermark?: string;
  bgOverride?: string;
}

export function planToSVG(plan: RenderPlan, opts: SVGOptions = {}): string {
  const W = plan.canvas.width;
  const H = plan.canvas.height;
  const p = [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"${opts.responsive ? ' style="width:100%;height:auto;display:block"' : ''}${opts.attrs ? ` ${opts.attrs}` : ''}>`,
  ];
  if (opts.white !== false) p.push(`<rect width="${W}" height="${H}" fill="#FFFFFF"/>`);
  if (plan.background) {
    p.push(
      imageSVG(
        plan.background.placement,
        opts.bgOverride !== undefined ? opts.bgOverride : plan.background.dataUrl,
        `bg|${plan.background.assetId}`,
      ),
    );
  }
  for (const it of plan.items) {
    if (it.kind === 'image') {
      if (!it.missing)
        p.push(imageSVG(it.placement as Placement, it.dataUrl as string, `ly|${it.layer.id}|${it.index}`));
      continue;
    }
    if (it.kind === 'qr') {
      const g = it.geo as BoxGeo;
      p.push(rot(it.rotation as number, g.x + g.size / 2, g.y + g.size / 2, qrSVG(it)));
      continue;
    }
    const l = it.layer;
    const g = it.geo as TextGeo;
    const texts = (it.lines as string[])
      .map(
        (ln, i) =>
          `<text x="${g.x.toFixed(1)}" y="${(g.baselines[i] as number).toFixed(1)}" text-anchor="${g.anchor}" font-family="${esc(l.font)}, sans-serif" font-weight="${l.weight}"${l.style === 'italic' ? ' font-style="italic"' : ''} font-size="${it.size}"${it.letterSpacingPx ? ` letter-spacing="${it.letterSpacingPx.toFixed(2)}"` : ''} fill="${l.color}"${(it.opacity as number) < 1 ? ` fill-opacity="${it.opacity}"` : ''}>${esc(ln)}</text>`,
      )
      .join('');
    p.push(rot(it.rotation as number, g.x, g.top + g.blockH / 2, texts));
  }
  if (opts.watermark) p.push(watermarkSVG(plan.canvas, opts.watermark));
  p.push('</svg>');
  return p.join('\n');
}

/** `Engine.toSVG` — the design-preview entry point; `failClosed` is off unless asked for. */
export function toSVG(
  layout: Layout,
  data: RenderData | null | undefined,
  opts: SVGOptions & { output?: OutputSpec; failClosed?: boolean } = {},
): string | null {
  const plan = resolveRenderPlan(layout, data, opts.output);
  if (!plan.ok && opts.failClosed) return null;
  return planToSVG(plan, opts);
}
